import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBrandConfig } from "@/hooks/use-brand-config";

/**
 * Fetches suppressed record IDs for a given table and the current brand entity
 * via the get_entity_suppressions Security Definer RPC.
 * Returns a Set<string> of record IDs that should be hidden.
 */
export function useEntitySuppressions(tableName: string): Set<string> {
  const [suppressedIds, setSuppressedIds] = useState<Set<string>>(new Set());
  const brand = useBrandConfig();

  useEffect(() => {
    if (!brand.isLoaded || !brand.entityId) {
      setSuppressedIds(new Set());
      return;
    }

    supabase
      .rpc("get_entity_suppressions", {
        _entity_id: brand.entityId,
        _table_name: tableName,
      })
      .then(({ data }) => {
        if (data && (data as any[]).length > 0) {
          setSuppressedIds(new Set((data as any[]).map((r: any) => r.record_id)));
        } else {
          setSuppressedIds(new Set());
        }
      });
  }, [brand.isLoaded, brand.entityId, tableName]);

  return suppressedIds;
}

/**
 * Non-hook version: fetch suppressed IDs for a table+entity combo via RPC.
 * Useful in non-component contexts (e.g., product-transmission.ts).
 */
export async function fetchEntitySuppressions(
  tableName: string,
  entityId: string | null | undefined
): Promise<Set<string>> {
  if (!entityId) return new Set();
  const { data } = await supabase.rpc("get_entity_suppressions", {
    _entity_id: entityId,
    _table_name: tableName,
  });
  if (data && (data as any[]).length > 0) {
    return new Set((data as any[]).map((r: any) => r.record_id));
  }
  return new Set();
}
